import { useCallback, useContext, useRef, useState } from 'react';
import AppContext from '../../context/AppContext';
import enhancedExportService from '../../services/enhancedExportService';
import pptxExportService from '../../services/pptxExportService';
import { customerNameForDocument, documentExportOptions } from './documentExportOptions';
import { exportBaseName } from './documentMeta';

// The options helpers live in their own module so pages can use them without loading the exporters.
export { customerNameForDocument, documentExportOptions };

/**
 * Export a document (Word, PDF, HTML or PowerPoint) and track which one is exporting.
 * @returns {{exporting: {id: string, type: string}|null, exportDocument: function(object, string, string, object=): Promise<boolean>}}
 *   `exportDocument(document, type, projectName, { customerName })` resolves true on success. Word,
 *   PDF and HTML use the document's page setup (headers, footers, margins, orientation).
 */
export default function useDocumentExport() {
  const [exporting, setExporting] = useState(null);
  // App state (optional: the hook also works outside the provider) for the document's customer.
  const app = useContext(AppContext);
  const stateRef = useRef(null);
  stateRef.current = app?.state || null;

  const exportDocument = useCallback(async (document, type, projectName = '', context = {}) => {
    if (!document?.content) return false;
    const base = exportBaseName(document.name);
    const customerName = context.customerName || customerNameForDocument(stateRef.current, document);
    const options = documentExportOptions(document, { projectName, customerName });
    setExporting({ id: document.id, type });
    try {
      switch (type) {
        case 'word':
          await enhancedExportService.exportToWord(document.content, `${base}.docx`, projectName, options);
          break;
        case 'pdf':
          await enhancedExportService.exportToPDF(document.content, `${base}.pdf`, projectName, options);
          break;
        case 'html':
          await enhancedExportService.exportToHTML(document.content, `${base}.html`, projectName, options);
          break;
        case 'pptx':
          // No page setup for slides; the deck row carries its template theme (trust_metadata).
          await pptxExportService.exportToPptx(document.content, `${base}.pptx`, projectName, { document });
          break;
        default:
          throw new Error('Unknown export type');
      }
      return true;
    } catch (error) {
      console.error(`${String(type).toUpperCase()} export failed:`, error);
      return false;
    } finally {
      setExporting(null);
    }
  }, []);

  return { exporting, exportDocument };
}
